// src/shared/image_thumbnail.ts
import * as FileSystem from 'expo-file-system/legacy'
import * as ImageManipulator from 'expo-image-manipulator'
import { Platform } from 'react-native'

interface ImageThumbnailProps {
  imageUri: string
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

interface ImageThumbnailResult {
  uri: string | null
  width?: number
  height?: number
}

/**
 * Generates a thumbnail for an image file
 * @param props Properties for thumbnail generation
 * @returns Promise resolving to the URI, width and height of the generated thumbnail
 */
export const generateImageThumbnail = async ({
  imageUri,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.7,
}: ImageThumbnailProps): Promise<ImageThumbnailResult> => {
  if (Platform.OS === 'web') {
    return generateThumbnailForWeb(imageUri, maxWidth, maxHeight, quality)
  } else {
    return generateThumbnailForMobile(imageUri, maxWidth, maxHeight, quality)
  }
}

/**
 * Generates a thumbnail for an image file on web
 */
const generateThumbnailForWeb = async (
  uri: string,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<ImageThumbnailResult> => {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image()
      // Only set crossOrigin for non-blob URLs; blob: URLs are same-origin
      // and setting crossOrigin on them causes the image to fail loading
      if (!uri.startsWith('blob:')) {
        img.crossOrigin = 'anonymous'
      }

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height)
          const dataUrl = canvas.toDataURL('image/jpeg', quality)
          resolve({
            uri: dataUrl,
            width,
            height,
          })
        } else {
          reject(new Error('Failed to get canvas context'))
        }
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      img.src = uri
    } catch (error) {
      console.warn('Error generating thumbnail for web:', error)
      reject(error)
    }
  })
}

/**
 * Generates a thumbnail for an image file on mobile
 *
 * Uses expo-image-manipulator to resize the image while maintaining aspect ratio
 */
const generateThumbnailForMobile = async (
  uri: string,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<ImageThumbnailResult> => {
  try {
    // Use ImageManipulator to resize the image
    const manipResult = await ImageManipulator.manipulateAsync(
      uri,
      [
        {
          resize: {
            height: maxHeight,
          },
        },
      ],
      {
        compress: quality,
        format: ImageManipulator.SaveFormat.JPEG,
      }
    )

    return {
      uri: manipResult.uri,
      width: manipResult.width,
      height: manipResult.height,
    }
  } catch (error) {
    console.warn('Error generating thumbnail with ImageManipulator:', error)

    // Fallback to simple copy if manipulation fails
    try {
      // Create a unique filename for the thumbnail
      const filename = `thumbnail_${Date.now()}.jpg`
      const thumbnailUri = `${FileSystem.cacheDirectory}${filename}`

      // Copy the file to the cache directory
      await FileSystem.copyAsync({
        from: uri,
        to: thumbnailUri,
      })

      return { uri: thumbnailUri }
    } catch (fallbackError) {
      console.warn('Fallback thumbnail generation failed:', fallbackError)
      return { uri: null }
    }
  }
}
